import React, { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX, Play, Pause, RotateCcw } from 'lucide-react';
import { audioPlayer } from '../services/audioApi';

const VoiceMessage = ({ 
  message, 
  selectedVoice = 'alloy',
  autoPlay = false,
  className = ''
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  const audioRef = useRef(null);
  const messageId = useRef(Math.random().toString(36).substring(7));

  // Check if message has audio data
  useEffect(() => {
    setHasAudio(!!(message?.audio?.data));
  }, [message]);

  // Auto-play if enabled and audio is available
  useEffect(() => {
    if (autoPlay && hasAudio && message?.audio?.data) {
      handlePlayAudio();
    }
  }, [autoPlay, hasAudio, message?.audio?.data]);

  const handlePlayAudio = async () => {
    if (isPlaying) {
      // Stop current playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      return;
    }

    try {
      setIsLoading(true);

      if (message?.audio?.data) {
        // Play audio from message data (base64)
        const audioBuffer = base64ToArrayBuffer(message.audio.data);
        await playAudioBuffer(audioBuffer);
      } else if (message?.content) {
        // Generate and play TTS for text content
        await audioPlayer.playText(message.content, selectedVoice);
      } else {
        throw new Error('No audio or text content available');
      }
    } catch (error) {
      console.error('Failed to play audio:', error);
      alert('Failed to play audio. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const playAudioBuffer = (audioBuffer) => {
    return new Promise((resolve, reject) => {
      try {
        const blob = new Blob([audioBuffer], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(blob);
        
        if (audioRef.current) {
          audioRef.current.pause();
        }

        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onloadstart = () => setIsLoading(true);
        audio.oncanplaythrough = () => setIsLoading(false);
        
        audio.onplay = () => setIsPlaying(true);
        audio.onpause = () => setIsPlaying(false);
        
        audio.onended = () => {
          setIsPlaying(false);
          URL.revokeObjectURL(audioUrl);
          resolve();
        };

        audio.onerror = (error) => {
          setIsPlaying(false);
          setIsLoading(false);
          URL.revokeObjectURL(audioUrl);
          reject(error);
        };

        audio.play();
      } catch (error) {
        setIsLoading(false);
        reject(error);
      }
    });
  };

  const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const handleRegenerate = async () => {
    if (!message?.content) return;
    
    try {
      setIsLoading(true);
      await audioPlayer.playText(message.content, selectedVoice);
    } catch (error) {
      console.error('Failed to regenerate audio:', error);
      alert('Failed to generate audio. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if no content
  if (!message?.content && !message?.audio?.data) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Play/Pause Button */}
      <button
        onClick={handlePlayAudio}
        disabled={isLoading}
        className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
          isPlaying
            ? 'bg-red-500 hover:bg-red-600 text-white'
            : 'bg-blue-500 hover:bg-blue-600 text-white'
        } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
        title={isPlaying ? 'Pause audio' : 'Play audio'}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-4 h-4" />
        ) : (
          <Play className="w-4 h-4 ml-0.5" />
        )}
      </button>

      {/* Audio type indicator */}
      <div className="flex items-center gap-1">
        {hasAudio ? (
          <div className="flex items-center gap-1 text-xs text-green-600">
            <Volume2 className="w-3 h-3" />
            <span>AI Audio</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs text-blue-600">
            <VolumeX className="w-3 h-3" />
            <span>TTS</span>
          </div>
        )}
      </div>

      {/* Regenerate button for TTS */}
      {!hasAudio && message?.content && (
        <button
          onClick={handleRegenerate}
          disabled={isLoading}
          className="p-1 rounded text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          title="Regenerate audio with current voice"
        >
          <RotateCcw className="w-3 h-3" />
        </button>
      )}

      {/* Voice indicator */}
      {!hasAudio && (
        <span className="text-xs text-gray-500 capitalize">
          {selectedVoice}
        </span>
      )}
    </div>
  );
};

export default VoiceMessage;